import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, Share2, FileText, 
  ChevronLeft, ChevronRight, Save, Menu, X,
  PanelRightClose, PanelRightOpen, Download, 
  History, Cloud, CloudOff, Check, RefreshCw, LogIn,
  Upload, FileUp
} from 'lucide-react';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { CompassRose, CompassNavSidebar, SubElementsList } from '../components/ui/compass-nav';
import { ToolsPanel } from '../components/ui/tools-panel';
import { BelongingFooter } from '../components/ui/belonging-footer';
import { AvatarStack } from '../components/ui/avatar';
import { 
  MandalaIcon, 
  CompassIcon as LegendaryCompass, 
  SoulSignalIcon, 
  TeamModeIcon, 
  SimpleModeIcon,
  SacredFounderIcon,
  LotusIcon,
  GoldenSpiralIcon,
  AncientSunIcon,
  BrandCheckerIcon,
  EmergencySOSIcon
} from '../components/ui/legendary-icons';
import { CollaborativeEditor } from '../components/ui/collaborative-editor';
import { RemindersPanel } from '../components/ui/reminders-panel';
import { EcosystemNav } from '../components/ui/ecosystem-nav';
import { SignInBanner, UserStatusIndicator, useAuth, syncCharterProgress } from '../components/ui/auth-components';
import { BrandChecker } from '../components/ui/brand-checker';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const BELONGING_SESSIONS_URL = 'https://belongingsessions.com';
const ELCHME_URL = 'https://elchme.com';
const LOCAL_STORAGE_PREFIX = 'refound_project_';

// Local storage helper for persistence
const localPersistence = {
  save: (projectId, data) => {
    try {
      const key = `${LOCAL_STORAGE_PREFIX}${projectId}`;
      const backup = {
        data,
        savedAt: new Date().toISOString(),
        version: Date.now()
      };
      localStorage.setItem(key, JSON.stringify(backup));
      return true;
    } catch (e) {
      console.error('Local save failed:', e);
      return false;
    }
  },
  
  load: (projectId) => {
    try {
      const key = `${LOCAL_STORAGE_PREFIX}${projectId}`;
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error('Local load failed:', e);
      return null;
    }
  },
  
  saveDraft: (projectId, sectionKey, subElementKey, content) => {
    try {
      const key = `${LOCAL_STORAGE_PREFIX}${projectId}_draft_${sectionKey}_${subElementKey}`;
      localStorage.setItem(key, JSON.stringify({
        content,
        savedAt: new Date().toISOString()
      }));
      return true;
    } catch (e) {
      return false;
    }
  },
  
  loadDraft: (projectId, sectionKey, subElementKey) => {
    try {
      const key = `${LOCAL_STORAGE_PREFIX}${projectId}_draft_${sectionKey}_${subElementKey}`;
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  },
  
  clearDraft: (projectId, sectionKey, subElementKey) => {
    try {
      const key = `${LOCAL_STORAGE_PREFIX}${projectId}_draft_${sectionKey}_${subElementKey}`;
      localStorage.removeItem(key);
    } catch (e) {
      // Ignore
    }
  }
};

export default function CompassStudio() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  // State
  const [project, setProject] = useState(null);
  const [sections, setSections] = useState([]);
  const [compassMeta, setCompassMeta] = useState([]);
  const [activeSection, setActiveSection] = useState('north');
  const [activeSubElement, setActiveSubElement] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [showToolsPanel, setShowToolsPanel] = useState(true);
  const [toolsPanelCollapsed, setToolsPanelCollapsed] = useState(false);
  const [users, setUsers] = useState([]);
  const [userFocus, setUserFocus] = useState({});
  const [editContent, setEditContent] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showConstitutionModal, setShowConstitutionModal] = useState(false);
  const [constitution, setConstitution] = useState(null);
  const [isGeneratingConstitution, setIsGeneratingConstitution] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [collaborativeMode, setCollaborativeMode] = useState(true); // Enable team attribution
  const [showSignInBanner, setShowSignInBanner] = useState(true);
  // Auto Generate state
  const [showAutoGenerate, setShowAutoGenerate] = useState(false);
  const [autoGenDescription, setAutoGenDescription] = useState('');
  const [isAutoGenerating, setIsAutoGenerating] = useState(false);
  const [showGeneratedDoc, setShowGeneratedDoc] = useState(false);
  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [showBrandChecker, setShowBrandChecker] = useState(false);
  // Document Upload state
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [uploadedDocContent, setUploadedDocContent] = useState('');
  const [uploadFileName, setUploadFileName] = useState('');
  const [isProcessingDocument, setIsProcessingDocument] = useState(false);
  const [reminders, setReminders] = useState([
    { id: '1', title: 'Finalize purpose statement', priority: 'high', dueDate: '2025-01-15', assignedTo: ['1'], completed: false },
    { id: '2', title: 'Schedule community call', priority: 'medium', dueDate: '2025-01-18', assignedTo: ['1', '2'], completed: false }
  ]);
  
  const userName = localStorage.getItem('blissabode_user_name') || 'Anonymous';
  const wsRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  
  // Handle sign in redirect
  const handleSignIn = () => {
    const returnUrl = encodeURIComponent(window.location.href);
    window.location.href = `${BELONGING_SESSIONS_URL}/login?redirect=${returnUrl}&app=blissabode`;
  };

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Back online! Syncing changes...');
      syncLocalChanges();
    };
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('You\'re offline. Changes will be saved locally.');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load project data
  useEffect(() => {
    loadProject();
    loadCompassMeta();
  }, [projectId]);

  // Setup WebSocket with reconnection
  useEffect(() => {
    if (!projectId || !userName) return;
    
    const connectWebSocket = () => {
      const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      const ws = new WebSocket(`${wsUrl}/ws/projects/${projectId}?user_name=${encodeURIComponent(userName)}`);
      
      ws.onopen = () => {
        console.log('WebSocket connected');
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'presence') {
          setUsers(data.data.users || []);
          // Extract focus data
          const focusMap = {};
          (data.data.users || []).forEach(u => {
            if (u.focus && u.name !== userName) {
              focusMap[u.name] = u.focus;
            }
          });
          setUserFocus(focusMap);
        } else if (data.type === 'sub_element_update') {
          // Update local state with remote changes (only if not our own)
          if (data.data.updated_by !== userName) {
            setSections(prev => prev.map(s => {
              if (s.key === data.data.section_key) {
                return {
                  ...s,
                  sub_elements: {
                    ...s.sub_elements,
                    [data.data.sub_element_key]: data.data.content
                  },
                  completion_percentage: data.data.completion_percentage
                };
              }
              return s;
            }));
            
            // If we're viewing the same element, update display
            if (data.data.section_key === activeSection && 
                data.data.sub_element_key === activeSubElement) {
              toast.info(`${data.data.updated_by} updated this section`);
            }
          }
        } else if (data.type === 'typing') {
          if (data.data.user !== userName) {
            setTypingUsers(prev => ({
              ...prev,
              [`${data.data.section_key}_${data.data.sub_element_key}`]: data.data.is_typing ? data.data.user : null
            }));
          }
        } else if (data.type === 'compass_navigate') {
          if (data.data.user !== userName) {
            // Show subtle notification
          }
        }
      };
      
      ws.onerror = (error) => console.error('WebSocket error:', error);
      
      ws.onclose = () => {
        console.log('WebSocket disconnected');
        // Attempt to reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          if (document.visibilityState === 'visible') {
            connectWebSocket();
          }
        }, 3000);
      };
      
      wsRef.current = ws;
    };
    
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [projectId, userName, activeSection, activeSubElement]);

  // Update edit content when section/sub-element changes
  useEffect(() => {
    const section = sections.find(s => s.key === activeSection);
    if (section && activeSubElement) {
      // Check for local draft first
      const draft = localPersistence.loadDraft(projectId, activeSection, activeSubElement);
      if (draft && draft.content) {
        const serverContent = section.sub_elements?.[activeSubElement] || '';
        if (draft.content !== serverContent && draft.content.length > serverContent.length) {
          // Draft has more content than server - ask user
          setEditContent(draft.content);
          setHasUnsavedChanges(true);
          toast.info('Restored unsaved draft');
        } else {
          setEditContent(serverContent);
          localPersistence.clearDraft(projectId, activeSection, activeSubElement);
        }
      } else {
        setEditContent(section.sub_elements?.[activeSubElement] || '');
      }
    } else {
      setEditContent('');
    }
    setHasUnsavedChanges(false);
  }, [activeSection, activeSubElement, sections, projectId]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const loadProject = async () => {
    try {
      // Try loading from local storage first for instant display
      const localBackup = localPersistence.load(projectId);
      if (localBackup) {
        setProject(localBackup.data.project);
        setSections(localBackup.data.sections || []);
        setCompassMeta(localBackup.data.compassMeta || []);
      }
      
      // Then fetch from server
      const response = await fetch(`${API_URL}/api/projects/${projectId}`);
      if (!response.ok) throw new Error('Project not found');
      
      const data = await response.json();
      setProject(data.project);
      setSections(data.sections || []);
      setCompassMeta(data.compass_sections_meta || []);
      setUsers(data.active_users || []);
      
      // Save to local storage for offline access
      localPersistence.save(projectId, {
        project: data.project,
        sections: data.sections,
        compassMeta: data.compass_sections_meta
      });
      
      // Set first sub-element as active
      const firstMeta = data.compass_sections_meta?.[0];
      if (firstMeta?.sub_elements?.[0]) {
        setActiveSubElement(firstMeta.sub_elements[0].key);
      }
    } catch (error) {
      console.error('Load error:', error);
      // If we have local data, continue with that
      const localBackup = localPersistence.load(projectId);
      if (localBackup) {
        toast.warning('Working offline with cached data');
      } else if (!isProcessingDocument) {
        // Only redirect if we're not in the middle of document processing
        toast.error('Failed to load project');
        navigate('/');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadCompassMeta = async () => {
    try {
      const response = await fetch(`${API_URL}/api/compass-sections`);
      if (response.ok) {
        const data = await response.json();
        setCompassMeta(data.sections || []);
      }
    } catch (error) {
      console.error('Failed to load compass meta:', error);
    }
  };

  const syncLocalChanges = async () => {
    // Sync any pending local changes when back online
    // This is handled automatically by the auto-save mechanism
  };

  const saveSubElement = useCallback(async (content, force = false) => {
    if (!activeSection || !activeSubElement) return;
    
    // Always save locally first for persistence guarantee
    localPersistence.saveDraft(projectId, activeSection, activeSubElement, content);
    
    if (!isOnline && !force) {
      setHasUnsavedChanges(true);
      toast.info('Saved locally (offline)');
      return;
    }
    
    setIsSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/projects/${projectId}/sections/${activeSection}/sub-element`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content,
            updated_by: userName,
            sub_element_key: activeSubElement
          })
        }
      );
      
      if (!response.ok) throw new Error('Save failed');
      
      const updatedSection = await response.json();
      setSections(prev => prev.map(s => 
        s.key === activeSection ? updatedSection : s
      ));
      
      // Clear local draft after successful save
      localPersistence.clearDraft(projectId, activeSection, activeSubElement);
      
      // Update local backup
      localPersistence.save(projectId, {
        project,
        sections: sections.map(s => s.key === activeSection ? updatedSection : s),
        compassMeta
      });
      
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
      
    } catch (error) {
      console.error('Save error:', error);
      setHasUnsavedChanges(true);
      toast.error('Failed to save to server. Changes saved locally.');
    } finally {
      setIsSaving(false);
    }
  }, [projectId, activeSection, activeSubElement, userName, isOnline, project, sections, compassMeta]);

  // Auto-save with debounce
  const handleContentChange = (value) => {
    setEditContent(value);
    setHasUnsavedChanges(true);
    
    // Save draft locally immediately
    localPersistence.saveDraft(projectId, activeSection, activeSubElement, value);
    
    // Broadcast typing indicator
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        section_key: activeSection,
        sub_element_key: activeSubElement,
        is_typing: true
      }));
    }
    
    // Clear typing indicator after pause
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'typing',
          section_key: activeSection,
          sub_element_key: activeSubElement,
          is_typing: false
        }));
      }
    }, 2000);
    
    // Debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      saveSubElement(value);
    }, 1500);
  };

  const handleSectionChange = (sectionKey) => {
    // Save current content before switching
    if (hasUnsavedChanges && editContent) {
      saveSubElement(editContent);
    }
    
    setActiveSection(sectionKey);
    setShowMobileNav(false);
    
    // Set first sub-element
    const meta = compassMeta.find(m => m.key === sectionKey);
    if (meta?.sub_elements?.[0]) {
      setActiveSubElement(meta.sub_elements[0].key);
    }
    
    // Broadcast navigation
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'focus_change',
        section_key: sectionKey,
        sub_element_key: meta?.sub_elements?.[0]?.key
      }));
    }
  };

  const handleCopyJoinInfo = () => {
    const joinUrl = `${window.location.origin}/join/${projectId}`;
    const info = `Join "${project?.name}"\nURL: ${joinUrl}\nCode: ${project?.join_code}`;
    navigator.clipboard.writeText(info);
    toast.success('Join info copied! Share with your team.');
  };

  const navigateCompass = (direction) => {
    const orderedKeys = ['north', 'northeast', 'east', 'southeast', 'south', 'southwest', 'west', 'northwest'];
    const currentIndex = orderedKeys.indexOf(activeSection);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % orderedKeys.length;
    } else {
      newIndex = (currentIndex - 1 + orderedKeys.length) % orderedKeys.length;
    }
    
    handleSectionChange(orderedKeys[newIndex]);
  };

  const handleInsertFromAI = (text) => {
    const newContent = editContent ? `${editContent}\n\n${text}` : text;
    setEditContent(newContent);
    saveSubElement(newContent);
    toast.success('Added to charter!');
  };

  const handleGenerateConstitution = async () => {
    setIsGeneratingConstitution(true);
    setShowConstitutionModal(true);
    
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/constitution/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          project_id: projectId, 
          format: 'full',
          include_signatures: true 
        })
      });
      
      if (!response.ok) throw new Error('Generation failed');
      
      const data = await response.json();
      setConstitution(data);
      toast.success(`Constitution generated! ${data.completion_percentage}% complete`);
    } catch (error) {
      toast.error('Failed to generate constitution');
      setShowConstitutionModal(false);
    } finally {
      setIsGeneratingConstitution(false);
    }
  };

  const downloadConstitution = () => {
    if (!constitution) return;
    
    const blob = new Blob([constitution.constitution], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project?.name || 'Community'}_Constitution_v${constitution.version}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Constitution downloaded!');
  };

  // Auto Generate function for Community Charter
  const handleAutoGenerate = async (mode = 'sections') => {
    if (!autoGenDescription.trim()) {
      toast.error('Please describe your community first');
      return;
    }
    
    setIsAutoGenerating(true);
    
    try {
      const endpoint = mode === 'document' ? '/api/auto-generate/document' : '/api/auto-generate';
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: autoGenDescription,
          type: 'community',
          output_format: mode
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (mode === 'sections' && data.generated_content && !data.generated_content.parse_error) {
          // Update all sections with generated content
          for (const [key, content] of Object.entries(data.generated_content)) {
            // Parse the key format (e.g., "north_origin_story")
            const parts = key.split('_');
            const sectionKey = parts[0];
            const subElementKey = parts.slice(1).join('_');
            
            // Save each sub-element
            try {
              await fetch(
                `${API_URL}/api/projects/${projectId}/sections/${sectionKey}/sub-element`,
                {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    content,
                    updated_by: userName,
                    sub_element_key: subElementKey
                  })
                }
              );
            } catch (saveError) {
              console.error(`Failed to save ${key}:`, saveError);
            }
          }
          
          // Reload project to get updated data
          await loadProject();
          setShowAutoGenerate(false);
          toast.success(`Generated ${Object.keys(data.generated_content).length} sections! Review and edit as needed.`);
        } else if (mode === 'document') {
          // Show the generated document
          setGeneratedDocument(data.document);
          setShowGeneratedDoc(true);
          setShowAutoGenerate(false);
          toast.success('Community charter document generated!');
        } else {
          toast.error('Generation returned unexpected format');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to generate');
      }
    } catch (error) {
      console.error('Auto-generate error:', error);
      toast.error('Failed to auto-generate. Please try again.');
    } finally {
      setIsAutoGenerating(false);
    }
  };

  // Document Upload and Assimilation
  const handleDocumentUpload = async () => {
    if (!uploadedDocContent.trim()) {
      toast.error('Please paste your document content or upload a file');
      return;
    }
    
    if (!projectId) {
      toast.error('Project ID not found. Please refresh the page.');
      return;
    }
    
    console.log('Starting document upload for project:', projectId);
    console.log('Content length:', uploadedDocContent.length);
    
    setIsProcessingDocument(true);
    
    try {
      // Use AbortController for timeout (90 seconds for large documents with Claude)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.log('Request timed out after 90 seconds');
      }, 90000);
      
      const requestBody = {
        project_id: projectId,
        content: uploadedDocContent,
        document_type: 'text',
        filename: uploadFileName || 'uploaded_document.txt',
        studio_type: 'community'
      };
      
      console.log('Sending request to:', `${API_URL}/api/projects/${projectId}/upload-document`);
      
      const response = await fetch(`${API_URL}/api/projects/${projectId}/upload-document`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Response data:', data);
        
        if (data.success) {
          // Close modal first
          setShowDocumentUpload(false);
          setUploadedDocContent('');
          setUploadFileName('');
          setIsProcessingDocument(false);
          
          // Show success message
          toast.success(`Document processed! Content added to ${data.sections_updated.length} sections.`);
          
          // Reload the page to show updated content
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          console.error('Document processing failed:', data);
          toast.error(data.error || 'AI processing failed. Please try again.');
        }
      } else {
        const errorText = await response.text();
        console.error('Upload response error:', response.status, errorText);
        try {
          const errorData = JSON.parse(errorText);
          toast.error(errorData.detail || errorData.error || `Server error: ${response.status}`);
        } catch {
          toast.error(`Server error: ${response.status}`);
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Request was aborted (timeout)');
        toast.error('Request timed out. The document may be too large or the server is busy. Please try again.');
      } else {
        console.error('Document upload error:', error);
        console.error('Error details:', error.message, error.stack);
        toast.error(`Upload failed: ${error.message || 'Please try again.'}`);
      }
    } finally {
      setIsProcessingDocument(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setUploadFileName(file.name);
    
    // Handle text files directly
    if (file.type === 'text/plain' || file.name.endsWith('.txt') || file.name.endsWith('.md')) {
      const text = await file.text();
      setUploadedDocContent(text);
      toast.success(`Loaded ${file.name}`);
    } 
    // Handle PDF - extract text (simplified - actual PDF parsing would need a library)
    else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
      toast.info('PDF detected. For best results, please copy and paste the text content directly.');
      setUploadedDocContent('');
    }
    // Other document types
    else {
      const text = await file.text();
      setUploadedDocContent(text);
      toast.success(`Loaded ${file.name}`);
    }
  };

  // Get current section data
  const currentSection = sections.find(s => s.key === activeSection);
  const currentMeta = compassMeta.find(m => m.key === activeSection);
  const currentSubElementMeta = currentMeta?.sub_elements?.find(se => se.key === activeSubElement);
  
  // Calculate overall progress
  const overallProgress = sections.length > 0
    ? Math.round(sections.reduce((acc, s) => acc + (s.completion_percentage || 0), 0) / sections.length)
    : 0;
  
  // Check if someone else is typing in current element
  const currentTypingUser = typingUsers[`${activeSection}_${activeSubElement}`];
  
  // Get users viewing current section
  const usersInCurrentSection = Object.entries(userFocus)
    .filter(([name, focus]) => focus?.section === activeSection)
    .map(([name]) => name);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
        >
          <MandalaIcon size={80} className="text-[var(--primary)]" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg)] flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur bg-[var(--bg)]/90 border-b border-[var(--muted)]/50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                data-testid="back-button"
              >
                <ArrowLeft size={18} />
              </Button>
              
              <div className="flex items-center gap-3 cursor-pointer group" onClick={() => navigate('/')}>
                <div className="w-10 h-10 rounded-full bg-[var(--primary)] flex items-center justify-center group-hover:bg-[var(--primary-hover)] group-hover:scale-105 transition-all">
                  <LegendaryCompass size={20} className="text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="font-heading text-lg font-semibold text-[var(--fg)] group-hover:text-[var(--primary)] transition-colors">
                      {project?.name || 'Charter'}
                    </h1>
                    {/* Status indicators */}
                    <div className="flex items-center gap-1">
                      {isOnline ? (
                        <Cloud size={14} className="text-[var(--success)]" />
                      ) : (
                        <CloudOff size={14} className="text-[var(--warning)]" />
                      )}
                      {hasUnsavedChanges && (
                        <span className="w-2 h-2 rounded-full bg-[var(--warning)]" />
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-[var(--fg)]/60">
                    {overallProgress}% Complete • {users.length} collaborator{users.length !== 1 ? 's' : ''} online
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Quick Tool Icons - Premium styling */}
              <div className="flex items-center gap-2 mr-3">
                {/* Brand Checker Icon */}
                <button
                  onClick={() => setShowBrandChecker(true)}
                  className="group relative w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 via-orange-500/15 to-yellow-500/20 border border-amber-400/30 hover:border-amber-400/60 hover:from-amber-500/30 hover:via-orange-500/25 hover:to-yellow-500/30 transition-all duration-300 flex items-center justify-center shadow-lg shadow-amber-500/10 hover:shadow-amber-500/20"
                  title="Brand Name Checker"
                  data-testid="brand-checker-icon"
                >
                  <BrandCheckerIcon size={20} className="text-amber-300 group-hover:text-amber-200 transition-colors" />
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-amber-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                
                {/* Emergency SOS Icon */}
                <a
                  href={ELCHME_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500/20 via-red-500/15 to-pink-500/20 border border-rose-400/30 hover:border-rose-400/60 hover:from-rose-500/30 hover:via-red-500/25 hover:to-pink-500/30 transition-all duration-300 flex items-center justify-center shadow-lg shadow-rose-500/10 hover:shadow-rose-500/20"
                  title="Emergency Empathy Alchemy - elchme.com"
                  data-testid="emergency-sos-icon"
                >
                  <EmergencySOSIcon size={20} className="text-rose-300 group-hover:text-rose-200 transition-colors" />
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-rose-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              </div>

              {/* Presence */}
              {users.length > 0 && (
                <div className="hidden md:flex items-center gap-2 mr-2">
                  <AvatarStack users={users.map(u => ({ name: u.name || u }))} max={4} />
                </div>
              )}
              
              {/* Auto Generate Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAutoGenerate(true)}
                className="hidden md:flex text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                data-testid="auto-generate-button"
              >
                <GoldenSpiralIcon size={18} className="text-purple-400" />
                <span className="hidden lg:inline ml-2">Auto Generate</span>
              </Button>
              
              {/* Upload Document Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDocumentUpload(true)}
                className="hidden md:flex text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                data-testid="upload-document-button"
              >
                <Upload size={18} className="text-amber-400" />
                <span className="hidden lg:inline ml-2">Import Doc</span>
              </Button>
              
              {/* Startup Studio Link */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/startup')}
                className="hidden md:flex text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10"
                data-testid="startup-studio-link"
              >
                <SacredFounderIcon size={18} className="text-indigo-400" />
                <span className="hidden lg:inline ml-2">Startup Studio</span>
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyJoinInfo}
                data-testid="share-button"
              >
                <Share2 size={16} />
                <span className="hidden sm:inline ml-2">Invite Team</span>
              </Button>
              
              <Button
                variant="secondary"
                size="sm"
                onClick={handleGenerateConstitution}
                disabled={overallProgress < 25}
                data-testid="generate-constitution-button"
              >
                <FileText size={16} />
                <span className="hidden sm:inline ml-2">
                  {overallProgress < 25 ? 'Need 25% to Generate' : 'Generate Constitution'}
                </span>
              </Button>

              {/* Save indicator */}
              {isSaving && (
                <div className="flex items-center gap-1 text-xs text-[var(--fg)]/50">
                  <RefreshCw size={12} className="animate-spin" />
                  <span className="hidden sm:inline">Saving...</span>
                </div>
              )}
              {!isSaving && lastSaved && (
                <div className="hidden sm:flex items-center gap-1 text-xs text-[var(--success)]">
                  <Check size={12} />
                  <span>Saved</span>
                </div>
              )}

              {/* Tools Panel Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setToolsPanelCollapsed(!toolsPanelCollapsed)}
                className="hidden lg:flex text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/20 border border-emerald-500/30"
                data-testid="toggle-tools-panel"
                title={toolsPanelCollapsed ? "Show Tools Panel" : "Hide Tools Panel"}
              >
                {toolsPanelCollapsed ? <PanelRightOpen size={18} /> : <PanelRightClose size={18} />}
              </Button>
              
              {/* Mobile nav toggle */}
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={() => setShowMobileNav(!showMobileNav)}
              >
                {showMobileNav ? <X size={20} /> : <Menu size={20} />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 container mx-auto px-4 py-6">
        <div className={`grid grid-cols-1 gap-6 ${
          toolsPanelCollapsed 
            ? 'lg:grid-cols-[300px_1fr_60px]' 
            : 'lg:grid-cols-[280px_1fr_340px]'
        }`}>
          
          {/* Left sidebar - Compass Navigation */}
          <aside className={`${
            showMobileNav ? 'fixed inset-0 z-40 bg-[var(--bg)] p-4 overflow-auto' : 'hidden'
          } lg:block lg:static lg:bg-transparent lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto lg:sticky lg:top-24`}>
            {showMobileNav && (
              <div className="flex justify-between items-center mb-4 lg:hidden">
                <h2 className="font-heading text-lg">Compass Navigation</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowMobileNav(false)}>
                  <X size={20} />
                </Button>
              </div>
            )}
            
            {/* Compass Rose Visual */}
            <div className="mb-6 flex justify-center py-4 px-2">
              <CompassRose
                size={220}
                activePoint={activeSection}
                sections={sections}
                onPointClick={handleSectionChange}
              />
            </div>
            
            {/* Section list */}
            <CompassNavSidebar
              sections={sections}
              activeSection={activeSection}
              onSectionClick={handleSectionChange}
              compassMeta={compassMeta}
            />
          </aside>

          {/* Center - Content Editor */}
          <div className="space-y-6">
            {/* Section header */}
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <div 
                className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${currentMeta?.color}20` }}
              >
                <span 
                  className="font-heading text-2xl font-bold"
                  style={{ color: currentMeta?.color }}
                >
                  {currentMeta?.direction}
                </span>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl text-[var(--fg)] mb-2">
                {currentMeta?.title}
              </h2>
              <p className="text-sm text-[var(--fg)]/70 italic">
                {currentMeta?.subtitle}
              </p>
              <p className="text-sm text-[var(--fg)]/60 mt-2 max-w-xl mx-auto">
                {currentMeta?.description}
              </p>
              
              {/* Users in this section */}
              {usersInCurrentSection.length > 0 && (
                <div className="mt-3 flex items-center justify-center gap-2">
                  <Users size={14} className="text-[var(--info)]" />
                  <span className="text-xs text-[var(--info)]">
                    {usersInCurrentSection.join(', ')} {usersInCurrentSection.length === 1 ? 'is' : 'are'} also here
                  </span>
                </div>
              )}
            </motion.div>

            {/* Sub-elements navigation */}
            <SubElementsList
              subElements={currentMeta?.sub_elements || []}
              subElementsContent={currentSection?.sub_elements || {}}
              activeSubElement={activeSubElement}
              onSubElementClick={setActiveSubElement}
            />

            {/* Content editor */}
            {activeSubElement && currentSubElementMeta && (
              <motion.div
                key={`${activeSection}-${activeSubElement}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <SoulSignalIcon size={18} className="text-[var(--accent)]" />
                        {currentSubElementMeta.title}
                      </CardTitle>
                      {currentTypingUser && (
                        <Badge variant="outline" className="text-xs animate-pulse">
                          {currentTypingUser} is typing...
                        </Badge>
                      )}
                    </div>
                    <CardDescription className="text-sm">
                      {currentSubElementMeta.prompt}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <CollaborativeEditor
                      content={editContent}
                      onChange={handleContentChange}
                      userName={userName}
                      placeholder="Begin writing your response here... Your work is automatically saved."
                      isCollaborative={collaborativeMode && users.length > 0}
                      showAttribution={collaborativeMode}
                      className="min-h-[200px]"
                    />
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-xs text-[var(--fg)]/50 italic max-w-md">
                        &ldquo;{currentSubElementMeta.guidance}&rdquo;
                      </p>
                      <div className="flex items-center gap-2">
                        {/* Collaborative mode toggle */}
                        <button
                          onClick={() => setCollaborativeMode(!collaborativeMode)}
                          className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-all ${
                            collaborativeMode 
                              ? 'bg-[var(--primary)]/20 text-[var(--primary)] border border-[var(--primary)]/30' 
                              : 'bg-[var(--muted)]/30 text-[var(--fg)]/50 border border-transparent hover:border-[var(--muted)]'
                          }`}
                          title={collaborativeMode ? 'Show author attribution' : 'Simple edit mode'}
                          data-testid="collab-mode-toggle"
                        >
                          {collaborativeMode ? (
                            <>
                              <TeamModeIcon size={16} />
                              <span>Team</span>
                            </>
                          ) : (
                            <>
                              <SimpleModeIcon size={16} />
                              <span>Solo</span>
                            </>
                          )}
                        </button>
                        {hasUnsavedChanges && (
                          <span className="text-xs text-[var(--warning)]">Unsaved changes</span>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => saveSubElement(editContent, true)}
                          disabled={isSaving}
                        >
                          {isSaving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Navigation buttons */}
            <div className="flex justify-between pt-4">
              <Button
                variant="ghost"
                onClick={() => navigateCompass('prev')}
                data-testid="prev-compass-point"
              >
                <ChevronLeft size={18} />
                Previous Point
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigateCompass('next')}
                data-testid="next-compass-point"
              >
                Next Point
                <ChevronRight size={18} />
              </Button>
            </div>
          </div>

          {/* Right sidebar - Tools Panel */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 h-[calc(100vh-140px)]">
              <ToolsPanel
                projectId={projectId}
                sectionKey={activeSection}
                subElementKey={activeSubElement}
                context={editContent}
                onInsert={handleInsertFromAI}
                isCollapsed={toolsPanelCollapsed}
                onToggleCollapse={() => setToolsPanelCollapsed(!toolsPanelCollapsed)}
                reminders={reminders}
                onAddReminder={(r) => setReminders([...reminders, r])}
                onToggleReminder={(id) => setReminders(reminders.map(r => r.id === id ? { ...r, completed: !r.completed } : r))}
                onDeleteReminder={(id) => setReminders(reminders.filter(r => r.id !== id))}
                showStartupLink={true}
                theme="earth"
                studioType="community"
              />
            </div>
          </aside>
        </div>
      </main>

      {/* Mobile Tools Panel Toggle */}
      <div className="lg:hidden fixed bottom-4 right-4 z-40">
        <Button
          onClick={() => setShowToolsPanel(!showToolsPanel)}
          className="w-14 h-14 rounded-full shadow-lg bg-[var(--primary)]"
          data-testid="mobile-tools-toggle"
        >
          {showToolsPanel ? <X size={24} /> : <PanelRightOpen size={24} />}
        </Button>
      </div>

      {/* Mobile Tools Panel */}
      <AnimatePresence>
        {showToolsPanel && (
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            className="lg:hidden fixed inset-x-0 bottom-0 z-50 bg-[var(--card)] rounded-t-2xl shadow-2xl max-h-[85vh] overflow-hidden"
          >
            <div className="p-4 border-b border-[var(--muted)]">
              <div className="flex justify-between items-center">
                <h3 className="font-heading text-lg">Tools</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowToolsPanel(false)}>
                  <X size={20} />
                </Button>
              </div>
            </div>
            <div className="overflow-auto" style={{ maxHeight: 'calc(85vh - 60px)' }}>
              <ToolsPanel
                projectId={projectId}
                sectionKey={activeSection}
                subElementKey={activeSubElement}
                context={editContent}
                onInsert={(text) => {
                  handleInsertFromAI(text);
                  setShowToolsPanel(false);
                }}
                reminders={reminders}
                onAddReminder={(r) => setReminders([...reminders, r])}
                onToggleReminder={(id) => setReminders(reminders.map(r => r.id === id ? { ...r, completed: !r.completed } : r))}
                onDeleteReminder={(id) => setReminders(reminders.filter(r => r.id !== id))}
                showStartupLink={true}
                theme="earth"
                studioType="community"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Constitution Modal */}
      <AnimatePresence>
        {showConstitutionModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => !isGeneratingConstitution && setShowConstitutionModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[var(--card)] rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-[var(--muted)]">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-heading text-2xl text-[var(--fg)]">
                      {isGeneratingConstitution ? 'Generating Your Constitution...' : 'Community Constitution'}
                    </h2>
                    {constitution && (
                      <p className="text-sm text-[var(--fg)]/60">
                        Version {constitution.version} • {constitution.completion_percentage}% Complete
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {constitution && (
                      <Button variant="secondary" size="sm" onClick={downloadConstitution}>
                        <Download size={16} className="mr-2" />
                        Download
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => setShowConstitutionModal(false)}>
                      <X size={20} />
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="p-6 overflow-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
                {isGeneratingConstitution ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                    >
                      <MandalaIcon size={80} className="text-[var(--primary)]" />
                    </motion.div>
                    <p className="mt-6 text-[var(--fg)]/70">
                      M.ai.ry is weaving your community&apos;s wisdom into a sacred document...
                    </p>
                  </div>
                ) : constitution ? (
                  <div className="prose prose-lg max-w-none dark:prose-invert">
                    <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed bg-transparent p-0">
                      {constitution.constitution}
                    </pre>
                  </div>
                ) : null}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Auto Generate Modal */}
      <AnimatePresence>
        {showAutoGenerate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
            onClick={() => setShowAutoGenerate(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="w-full max-w-2xl bg-[var(--card)] rounded-2xl border border-[var(--muted)]/50 shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-[var(--muted)]/50 bg-gradient-to-r from-purple-600/20 via-emerald-600/20 to-amber-600/20">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-emerald-600 flex items-center justify-center">
                    <GoldenSpiralIcon size={28} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[var(--fg)]">Auto Generate</h2>
                    <p className="text-sm text-[var(--fg)]/60">AI-powered community charter generation</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[var(--fg)]/80 mb-2">
                    Describe your community in a few sentences
                  </label>
                  <textarea
                    value={autoGenDescription}
                    onChange={(e) => setAutoGenDescription(e.target.value)}
                    placeholder="e.g., A co-living community of 20-30 people focused on sustainable living and personal growth. We share meals, resources, and support each other's creative projects. Located in a rural setting with shared gardens and workshop spaces..."
                    className="w-full h-32 px-4 py-3 bg-[var(--bg)] border border-[var(--muted)]/50 rounded-xl text-[var(--fg)] placeholder:text-[var(--fg)]/30 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none resize-none"
                    data-testid="auto-generate-description"
                  />
                  <p className="text-xs text-[var(--fg)]/40 mt-2">
                    Include: what kind of community, who lives there, what you do together, what values matter
                  </p>
                </div>

                <div className="bg-[var(--bg)] rounded-xl p-4 border border-[var(--muted)]/30">
                  <p className="text-sm text-[var(--fg)]/70 mb-3">Choose what to generate:</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={() => handleAutoGenerate('sections')}
                      disabled={isAutoGenerating || !autoGenDescription.trim()}
                      className="flex-col h-auto py-4 bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 hover:from-emerald-500/30 hover:to-emerald-600/20 border border-emerald-500/30 text-[var(--fg)]"
                      data-testid="auto-generate-sections-btn"
                    >
                      {isAutoGenerating ? (
                        <RefreshCw size={20} className="mb-2 animate-spin" />
                      ) : (
                        <LegendaryCompass size={24} className="mb-2 text-emerald-400" />
                      )}
                      <span className="text-sm font-medium">Fill All Sections</span>
                      <span className="text-xs text-[var(--fg)]/50 mt-1">Complete 8-point compass</span>
                    </Button>
                    <Button
                      onClick={() => handleAutoGenerate('document')}
                      disabled={isAutoGenerating || !autoGenDescription.trim()}
                      className="flex-col h-auto py-4 bg-gradient-to-br from-purple-500/20 to-purple-600/10 hover:from-purple-500/30 hover:to-purple-600/20 border border-purple-500/30 text-[var(--fg)]"
                      data-testid="auto-generate-document-btn"
                    >
                      {isAutoGenerating ? (
                        <RefreshCw size={20} className="mb-2 animate-spin" />
                      ) : (
                        <LotusIcon size={24} className="mb-2 text-purple-400" />
                      )}
                      <span className="text-sm font-medium">Generate Charter</span>
                      <span className="text-xs text-[var(--fg)]/50 mt-1">Instant community charter</span>
                    </Button>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-amber-400/70 text-xs">
                  <AncientSunIcon size={16} className="text-amber-400" />
                  <span>Generated content is a starting point. Review and customize for your specific community.</span>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-[var(--muted)]/30 flex justify-end">
                <Button
                  variant="ghost"
                  onClick={() => setShowAutoGenerate(false)}
                  className="text-[var(--fg)]/60 hover:text-[var(--fg)]"
                >
                  Cancel
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Generated Document Modal */}
      <AnimatePresence>
        {showGeneratedDoc && generatedDocument && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowGeneratedDoc(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[var(--card)] rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-[var(--muted)]">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-heading text-2xl text-[var(--fg)]">
                      Generated Community Charter
                    </h2>
                    <p className="text-sm text-[var(--fg)]/60">
                      AI-generated starting point for your community
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        const blob = new Blob([generatedDocument], { type: 'text/markdown' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${project?.name || 'Community'}_Charter_Draft.md`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                        toast.success('Charter downloaded!');
                      }}
                    >
                      <Download size={16} className="mr-2" />
                      Download
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setShowGeneratedDoc(false)}>
                      <X size={20} />
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="p-6 overflow-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
                <div className="prose prose-lg max-w-none dark:prose-invert">
                  <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed bg-transparent p-0">
                    {generatedDocument}
                  </pre>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BelongingFooter />
      
      {/* Document Upload Modal */}
      <AnimatePresence>
        {showDocumentUpload && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowDocumentUpload(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[var(--card)] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-[var(--muted)]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                      <FileUp size={24} className="text-amber-400" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-[var(--fg)]">Import Document</h2>
                      <p className="text-sm text-[var(--fg)]/60">AI will analyze and distribute content to sections</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDocumentUpload(false)}
                    className="p-2 hover:bg-[var(--muted)]/30 rounded-lg transition-colors"
                  >
                    <X size={20} className="text-[var(--fg)]/60" />
                  </button>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                {/* File Upload */}
                <div className="border-2 border-dashed border-[var(--muted)] rounded-xl p-6 text-center hover:border-amber-400/50 transition-colors">
                  <input
                    type="file"
                    accept=".txt,.md,.doc,.docx,.pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="document-upload-input"
                  />
                  <label 
                    htmlFor="document-upload-input"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <Upload size={32} className="text-amber-400/60" />
                    <span className="text-sm text-[var(--fg)]/70">
                      Click to upload a document
                    </span>
                    <span className="text-xs text-[var(--fg)]/40">
                      .txt, .md, or paste content below
                    </span>
                  </label>
                </div>
                
                {uploadFileName && (
                  <div className="flex items-center gap-2 p-2 bg-amber-500/10 rounded-lg">
                    <FileText size={16} className="text-amber-400" />
                    <span className="text-sm text-[var(--fg)]">{uploadFileName}</span>
                  </div>
                )}
                
                {/* Text Paste Area */}
                <div>
                  <label className="text-sm font-medium text-[var(--fg)]/70 mb-2 block">
                    Or paste your document content:
                  </label>
                  <Textarea
                    value={uploadedDocContent}
                    onChange={(e) => setUploadedDocContent(e.target.value)}
                    placeholder="Paste your existing charter, business plan, manifesto, or any document here...

The AI will analyze this content and automatically distribute relevant sections to the appropriate compass points."
                    className="min-h-[200px] text-sm"
                  />
                  <p className="text-xs text-[var(--fg)]/40 mt-1">
                    {uploadedDocContent.length > 0 ? `${uploadedDocContent.length.toLocaleString()} characters` : 'Supports up to 500,000 characters (~250 pages)'}
                  </p>
                </div>
                
                {/* Info Box */}
                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <div className="flex items-start gap-3">
                    <AncientSunIcon size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-300 mb-1">How it works</p>
                      <ul className="text-xs text-[var(--fg)]/60 space-y-1">
                        <li>• Claude AI (200K context) analyzes your entire document</li>
                        <li>• Content is intelligently distributed to all 8 compass sections</li>
                        <li>• Supports documents up to ~250 pages (500K characters)</li>
                        <li>• Existing work is preserved and augmented</li>
                        <li>• Review and refine after import</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="p-4 border-t border-[var(--muted)] flex justify-between items-center">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowDocumentUpload(false);
                    setUploadedDocContent('');
                    setUploadFileName('');
                  }}
                  className="text-[var(--fg)]/60"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDocumentUpload}
                  disabled={!uploadedDocContent.trim() || isProcessingDocument}
                  className="bg-amber-500 hover:bg-amber-400 text-black"
                >
                  {isProcessingDocument ? (
                    <>
                      <RefreshCw size={16} className="animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Upload size={16} className="mr-2" />
                      Import & Analyze
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Brand Checker Modal */}
      <AnimatePresence>
        {showBrandChecker && (
          <BrandChecker 
            onClose={() => setShowBrandChecker(false)}
            projectId={projectId}
          />
        )}
      </AnimatePresence>
      
      {/* Ecosystem Navigation */}
      <EcosystemNav currentSite="community-forge" />
    </div>
  );
}
